import labelsDataRepository from "./labels.mjs";

if ( typeof window.glob !== 'object' ) {

	// -----------------------------------------------------------------------------------------------------------------
	/** { Object } */
	const glob = {
		/** @private */
		prop: { registered: {}, status: {}, data: {} }
	};

	glob.prop.keyApplicationName = 'my-app';
	glob.prop.isDeploy = ( process.env.NODE_ENV === "production" );
	glob.prop.appVersion = window.document.querySelector("head > meta[name='app-version']").getAttribute("content") || '';
	glob.prop.uri = { server: "" };
	glob.prop.uri.onlinebackend = '/backend/';
	glob.prop.uri.offlinebackend = 'http://localhost:3077/backend/';
	glob.prop.uri.api = glob.prop.uri[ glob.prop.isDeploy ? 'onlinebackend' : 'offlinebackend'];
	glob.prop.uri.localpath = window.location.pathname;
	glob.prop.uri.web = glob.prop.uri.server + glob.prop.uri.localpath;
	glob.prop.appRootNode = "appRootNode";
	glob.prop.homePage = 'MainMenu';
	glob.prop.labelsDB = labelsDataRepository();
	glob.prop.usernameKey = 'username';
	glob.prop.loginApi = 'login';
	glob.prop.xAppHeader = 'X-App-ID';
	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * @function
	 * @param {GlobSetupOptions} options
	 * @return {void}
	 */
	glob.setupApp = ( options= {} ) => {
		if ( options.homePage ) glob.prop.homePage = options.homePage;
		if ( options.appRootNode ) glob.prop.appRootNode = options.appRootNode;
		if ( options.keyApplicationName ) glob.prop.keyApplicationName = options.keyApplicationName;
		if ( typeof options.isDeploy === 'boolean' ) glob.prop.isDeploy = options.isDeploy;
		if ( options.appVersion ) glob.prop.appVersion = options.appVersion;
		if ( options.usernameKey ) glob.prop.usernameKey = options.usernameKey;
		if ( typeof options.uri === 'object') {
			Object.entries(options.uri).forEach( ( [ k, v ] ) => {
				glob.prop.uri[k] = v;
			});
			if ( ! options.uri.api ) glob.prop.uri.api = glob.prop.uri[ glob.prop.isDeploy ? 'onlinebackend' : 'offlinebackend'];
			if ( ! options.uri.web ) glob.prop.uri.web = glob.prop.uri.server + glob.prop.uri.localpath;
		}
		glob.WebAppInit();
	}

	/**
	 * @private
	 * @function
	 */
	glob.WebAppInit = ()=>{
		if ( window.matchMedia('(display-mode: standalone)').matches ) document.body.classList.add('standalone');
		if ( ! glob.prop.loadedComponents ) glob.prop.loadedComponents = { labels: true };
		glob.setLanguage();
		Object.entries( (glob.getStorageRepo()||{}) ).forEach( ([k,v]) => {
			glob.setStatus(k,v);
		});
		glob.prop.loadedComponents.core = true;
		if ( ! window.document.getElementById(glob.prop.appRootNode) ) {
			const r = document.createElement('div');
			r.setAttribute('id', glob.prop.appRootNode);
			document.body.appendChild(r);
		}
	};

	/**
	 * @function
	 * @param { string|false } [prefix]
	 * @return { string }
	 * @description Generates a UUID prefixed with some item type - defaults to "item"
	 */
	glob.generateUid = ( prefix = "item") => {
		return (prefix ? prefix+'-' : '') + ("10000000-1048-4000-8000-1400800110084").replace(/[018]/g, (c) =>
			(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
		);
	};

	/**
	 * @function
	 * @param {*} x
	 * @return {undefined|*}
	 */
	glob.clone = (x) => {
		if (typeof x === "undefined") return undefined;
		if ( x && typeof x === 'object' ) try {
			return window.structuredClone ? window.structuredClone(x) : JSON.parse(JSON.stringify(x));
		} catch (e) { }
		return x;
	};

	/**
	 * @function
	 * @return {string} The app version declared in package.json
	 * @description Returns app version declared in package.json
	 */
	glob.getAppVersion = () => process.env.REACT_APP_VERSION;

	/**
	 * @private
	 * @function
	 * @return {void}
	 * @description Only in production: checks if app needs to be updated, if yes reloads.
	 */
	glob.checkForUpdate = () => {
		if ( ! glob.prop.isDeploy ) return;
		const curVersion = glob.getAppVersion();
		if ( ! curVersion ) return;
		let latestVersion;
		fetch(
			`${process.env.PUBLIC_URL}/index.html`,
			{
				method: 'GET',
				cache: 'no-cache',
				credentials: 'include',
				referrerPolicy: 'no-referrer',
				headers: { pragma: 'no-cache', 'cache-control': 'no-cache' }
			}
		)
			.then(res => res.text())
			.then(htmlString => {
				let doc = new DOMParser().parseFromString(htmlString, "text/html");
				latestVersion = doc
					.querySelector("head > meta[name='app-version']")
					.getAttribute("content");
				if (latestVersion !== curVersion) window.location.reload(true);
			})
			.catch( ()=>{
				console.error('Cannot contact server');
			})
	}

	if (glob.prop.isDeploy) {
		window.document.addEventListener(
			"visibilitychange",
			evt => {
				if ( evt.target.visibilityState === "visible" ) setTimeout(glob.checkForUpdate);
			}
		);
	}

	/**
	 * @function
	 * @param { string|[string]} contexts The name(s) ot the status change variable(s)
	 * @param { function } trigger
	 * @param { string } [uid]
	 * @return { (function(): void)|* }
	 * @description Register a function handle on a status change
	 */
	glob.register = ( contexts, trigger, uid ) => {
		if ( typeof contexts === 'string' ) contexts = [contexts];
		if ( typeof trigger !== 'function' ) {
			glob.unregister(contexts,uid);
			return;
		}
		if ( typeof uid !== 'string' ) uid = glob.generateUid();
		if ( Array.isArray(contexts) ) contexts.forEach( c => {
			try {
				if ( typeof glob.prop.registered[c] === 'undefined') glob.prop.registered[c] = {};
				glob.prop.registered[c][uid] = trigger;
			} catch(e) {
				console.log("glob.register", e, contexts, uid, trigger );
			}
		});
		return ()=>{ glob.unregister(contexts, uid); };
	};

	/**
	 * @function
	 * @param { string|[string]} contexts The name(s) ot the status change variable(s)
	 * @param {string} uid
	 * @return { void }
	 * @description Unregister a function handle on a status change
	 */
	glob.unregister = ( contexts, uid ) => {
		if ( typeof uid !== 'string' ) return;
		if ( typeof contexts === 'string' ) contexts = [contexts];
		if ( Array.isArray(contexts) ) contexts.forEach( c => {
			try {
				if ( typeof glob.prop.registered[c] === 'object') delete glob.prop.registered[c][uid];
			} catch(e) {
				console.log("glob.unregister", e, uid, contexts );
			}
		});
	};

	/**
	 * @function
	 * @param {string} uid
	 * @description De-register this UID from whatever it was registered
	 */
	glob.deregister = ( uid ) => {
		if (typeof uid !== 'string') return;
		Object.keys( glob.prop.registered || {} ).forEach( k => {
			delete glob.prop.registered[k][uid];
		});
	};

	/**
	 * @function
	 * @param { string } context The name ot the status change variable
	 * @param {any} args
	 * @return { Promise<void> }
	 * @description Trigger a status variable change to all registered handlers
	 */
	glob.trigger = async ( context, ...args ) => {
		if ( typeof context !== 'string' ) return;
		if ( glob.prop.registered[context] ) Object.entries(glob.prop.registered[context]).forEach(([uid,trigger])=>{
			if ( typeof trigger === 'function' ) setTimeout( ()=>{ trigger.apply(undefined, args); }, 10);
		});
	};

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @return {*}
	 * @description Get current value of a status variable
	 */
	glob.getStatus = ( s ) => { return glob.prop.status[s]; };

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @param { any } v The value to be set
	 * @param { boolean } [persist] Data should persist
	 * @return {void}
	 * @description Set a status variable
	 */
	glob.setStatus = ( s, v, persist ) => {
		glob.prop.status[s] = v;
		if ( persist ) glob.setStorage( s, v );
		glob.trigger( s, v  );
	};

	/**
	 * @function
	 * @param {string} s The status variable name
	 * @param {boolean} [broadcast] Broadcast the change with trigger (default: false)
	 * @return { void }
	 * @description Remove a status variable and all its registered broadcasts
	 */
	glob.dropStatus = ( s, broadcast ) => {
		if ( typeof s === "string" ) s = [s];
		if ( ! Array.isArray(s) ) return;
		s.forEach( x =>{
			delete glob.prop.status[x];
			if ( broadcast ) glob.trigger(x);
			delete glob.prop.registered[x];
		})
	};

	/**
	 * @function
	 * @return {?string}
	 * @description Display name of user
	 */
	glob.getUserLoginName = () => {
		return glob.getStatus('userLoginName') || "";
	};

	/**
	 * @function
	 * @return {?string}
	 * @description Login name of user
	 */
	glob.getUserLogin = () => {
		return (glob.getStatus('userLogin')||{})[glob.prop.usernameKey] || "";
	};

	/**
	 * @function
	 * @return {boolean}
	 */
	glob.userIsAdmin = () => {
		return !! glob.getStatus('userLogin')?.isAdmin;
	}

	/**
	 * @function
	 * @param {object} userData
	 * @return {void}
	 */
	glob.setUserLogin = ( userData ) => {
		glob.setStatus('userLoginName', (userData||{})[glob.prop.usernameKey] || '');
		glob.setStatus('userLogin', userData ?? false );
	}

	/**
	 * @function
	 * @param { string } auth
	 * @param { Record<string,string>} [headers]
	 * @return {Promise<Object>}
	 */
	glob.login = async ( auth, headers = {} ) => {
		if ( ! auth ) auth = window.sessionStorage.getItem(glob.prop.keyApplicationName+'-auth');
		if ( ! auth ) throw new Error('Missing credentials');
		headers['Authorization'] = 'Basic ' + auth;
		headers[glob.prop.xAppHeader] = glob.prop.keyApplicationName;
		try {
			const response = await fetch(
				glob.prop.uri.api + glob.prop.loginApi,
				{ cache: 'no-cache', headers: headers }
			)
			if ( ! response.ok ) {
				throw(new Error(String(response.status)+' Failed authentication request'));
			}
			const data = await response.json();
			glob.setUserLogin(data);
			window.sessionStorage.setItem(glob.prop.keyApplicationName+'-auth', auth);
			return(data);
		} catch (e) {
			window.sessionStorage.removeItem(glob.prop.keyApplicationName+'-auth');
			glob.setUserLogin();
			throw(e);
		}
	}

	/**
	 * @function
	 * @param {boolean} [force]
	 * @return { void }
	 */
	glob.logout = ( force ) => {
		window.document.querySelectorAll(' body > div:has(div.modal)').forEach( d =>{
			d.remove();
		})
		window.sessionStorage.removeItem(glob.prop.keyApplicationName+'-auth');
		glob.setUserLogin();
	}

	// -----------------------------------------------------------------------------------------------------------------

	/**
	 * @function
	 * @param { string } path
	 * @param { Object } [data]
	 * @param { string[]|function|object } [headers]
	 * @return { Promise<Object> }
	 * @description Performs an API request
	 */
	glob.api = async ( path, data = {}, headers = [] ) => {
		/** @type { string } */
		const uri = glob.prop.uri.api + path;
		/** @type { Request } */
		const req = {
			cache: 'no-cache',
			method: 'POST',
			body: JSON.stringify( data ),
			headers: {
				Authorization: 'Basic ' + window.sessionStorage.getItem(glob.prop.keyApplicationName+'-auth'),
				'Content-Type': 'application/json; charset=UTF-8',
				accept: 'application/json; charset=UTF-8, application/xml; charset=UTF-8',
				pragma: 'no-cache',
				'cache-control': 'no-cache'
			}
		};
		req.headers[glob.prop.xAppHeader] = glob.prop.keyApplicationName;
		if ( typeof headers === "function" ) {
			req.headers = headers( req.headers );
		} else if ( headers instanceof Array ) {
			headers.forEach( h => { req.headers[h] = headers[h]; })
		} else if ( typeof headers === 'object' ) {
			Object.entries(headers).forEach( ( [k,v]) => { req.headers[k] = v; } );
		}
		const response = await fetch( uri, req );
		if ( ! response.ok ) throw new Error(String(response.status)+' '+response.statusText );
		const cType = response.headers.get("content-type");
		if ( cType.includes("/json"))  return (await response.json());
		if ( cType.includes("text/"))  return (await response.text());
		const blob = await response.blob();
		const cDisp = response.headers.get("content-disposition");
		const fileName = (cDisp||'').replace(/^.*filename=['" ]*([^'" ;]+).*/i,"$1");
		if ( fileName ) {
			const url = window.URL.createObjectURL(blob);
			const a = window.document.createElement('a');
			a.href = url;
			a.download = fileName;
			window.document.body.appendChild(a);
			a.click();
			setTimeout(()=>{ a.remove(); },100);
			return fileName;
		}
		return [ blob, cType, cDisp ];
	}

	/**
	 * @function
	 * @param { Object } data
	 * @param { string } [filename]
	 * @return {void}
	 * @description Save on device a JSON file from data
	 */
	glob.saveJSON = (data, filename="output.json") => {
		if(!data) return;
		if(typeof data === "object") data = JSON.stringify(data, undefined, "\t");
		const blob = new Blob([data], {type: 'application/json'});
		const a = document.createElement('a');
		a.download = filename;
		a.href = window.URL.createObjectURL(blob);
		a.dataset.downloadurl =  ['application/json', a.download, a.href].join(':');
		a.click();
		setTimeout( ()=>{ a.remove() }, 1000 );
	}

	/**
	 * Return a file to upload
	 * @function
	 * @param {string} [accept]
	 * @return {Promise<Object>}
	 * @description Return object parsed from JSON file
	 */
	glob.upload = ( accept = ".json" ) => {
		if ( typeof accept === "string" ) accept = [accept];
		accept = accept.map( x => x.replace(/^\*?\.?/,'')).map( x => '.'+x );
		window.document.body.querySelectorAll(':scope > input.autoInputField').forEach( x => {
			x.remove();
		})
		const input = window.document.createElement('input');
		input.name = 'file';
		input.setAttribute('type', 'file');
		input.setAttribute('accept', accept);
		input.style.display = 'none';
		input.className = 'autoInputField';
		window.document.body.appendChild(input);
		return new Promise( ( resolve ) => {
			input.addEventListener('change', async () => {
				const selectedFile = input.files[0];
				if ( selectedFile ) {
					let formData = new FormData();
					formData.append("file", selectedFile);
					const reader = new window.FileReader();
					reader.addEventListener("load", () => {
						resolve({name: formData.get('file').name, content: reader.result});
					})
					reader.readAsDataURL(selectedFile);
				} else {
					resolve(false );
				}
				input.remove();
			});
			setTimeout( ()=>{ input.click(); } );
		});
	};

	/**
	 * @function
	 * @param {string} [accept]
	 * @return {Promise<Object>}
	 * @description Return object parsed from JSON file
	 */
	glob.uploadJSON = ( accept = ".json" ) => {
		if ( typeof accept === "string" ) accept = [accept];
		accept = accept.map( x => x.replace(/^\*?\.?/,'')).map( x => '.'+x );
		window.document.body.querySelectorAll(':scope > input.autoInputField').forEach( x => {
			x.remove();
		})
		const input = window.document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', accept);
		input.style.display = 'none';
		input.className = 'autoInputField';
		window.document.body.appendChild(input);
		return new Promise( ( resolve ) => {
			input.addEventListener('change', () => {
				const selectedFile = input.files[0];
				if ( selectedFile ) {
					const reader = new window.FileReader();
					reader.addEventListener( 'load', () => {
						try {
							resolve( JSON.parse(reader.result) );
						} catch(e) {
							console.error(e);
							resolve( {name: input.value, content: reader.result });
						}
					});
					reader.readAsText(selectedFile);
				} else {
					resolve(false );
				}
				input.remove();
			});
			setTimeout( ()=>{ input.click(); } );
		});
	}

	// -----------------------------------------------------------------------------------------------------------------

	//***** Navigation ********

	/** @typedef { import("./Glob").GlobPage } */
	/** @typedef { import("./Glob").GlobPageParams } */

	/**
	 * @private
	 * @param { GlobPage|string } page
	 * @return {GlobPage|GlobPageStatus}
	 */
	const checkPageFormat = page => {
		if (typeof page === "string") page = {name: page};
		if ( typeof page !== "object" ) return undefined;
		if ( typeof page.params !== 'object') page.params = {};
		return page;
	}

	/**
	 * @private
	 * @function
	 * @param { GlobPageParams } [params]
	 * @return {string}
	 */
	const params2hash = params => {
		const parts = [];
		if ( params && typeof params === "object") Object.keys(params)
			.filter(k => !!k)
			.filter( k => (typeof params[k] === 'string' || typeof params[k] === 'number') )
			.filter(k => ! k.startsWith('_'))
			.sort( (a,b) => a.toLowerCase().localeCompare(b.toLowerCase()) )
			.forEach(k => {
				let v = params[k];
				v ??= "";
				parts.push(`${k}=${v}`);
			});
		return parts.length ? '/'+parts.join("/") : '';
	};

	/**
	 * @private
	 * @param {string} [hash] Defailts to window.location.hash
	 * @return {GlobPage|undefined}
	 */
	const hash2page = ( hash = window.location.hash) => {
		if ( ! hash.startsWith("#!/")) return undefined;
		const parts = hash.split("/").slice(1);
		if ( parts[0] ) {
			const page = { name: parts.shift(), params: {}};
			parts.filter( s => (s.includes('=')) ).forEach( s => {
				const p = s.split("=");
				if ( p[0] ) {
					const k = p.shift();
					page.params[k] = p.join('=') || '';
				}
			})
			return page;
		}
		return undefined;
	}

	/**
	 * Retrieve current page in page internal representation, including params.
	 * @desc If not detectable, return homepage.
	 * @function
	 * @param {boolean} [force] Return homepage regardless of current URI
	 * @return { GlobPage|GlobPageStatus }
	 * @see {glob.getCurrentPage}
	 */
	glob.getHomePage = (force) => checkPageFormat(force ? glob.prop.homePage : (hash2page() || glob.prop.homePage));

	/**
	 * Similar to glob.getHomePage, searches first in status then in URI and homepage.
	 * @function
	 * @return {GlobPage}
	 * @see {glob.getHomePage}
	 */
	glob.getCurrentPage = () => checkPageFormat( glob.getStatus('currentPage') ) || glob.getHomePage();

	/**
	 * Convert a page into its URI representation
	 * @function
	 * @param {GlobPage|string} [page] The page object, defaults to current page
	 * @return {string}
	 */
	glob.page2hash = (page = glob.getCurrentPage() ) => {
		page = checkPageFormat(page);
		return page ? "#!/" + page.name + params2hash(page.params) : '';
	};

	/**
	 * Goto some page, with or without params, optionally with status change(s)
	 * @function
	 * @param {GlobPageStatus|string} page
	 * @param {boolean} [force] Because sometimes page needs to be changed even if page/params don't change.
	 * @return {void}
	 */
	glob.gotoPage = (page, force) => {
		page = checkPageFormat(page) || glob.getHomePage(true);
		if (!page) return;
		const ns = glob.clone(page.status);
		const prevPage = glob.clone( glob.getCurrentPage() );
		if ( prevPage ) glob.setStatus('previousPage',prevPage);
		const newHash = glob.page2hash(page);
		if (newHash !== window.location.hash) {
			window.history.pushState(null, page.name, glob.prop.uri.localpath + newHash);
			glob.setStatus('currentPage', page);
		} else if (force) {
			glob.setStatus('currentPage', page);
		}
		if ( ns ) {
			Object.entries(ns).forEach( ([k,v]) => {
				glob.setStatus(k,v);
			});
		}
	};

	/**
	 * Go to homepage.
	 * @function
	 * @return {void}
	 */
	glob.gotoHomePage = () => { glob.gotoPage() };

	/**
	 * Automatically detect (and go to) page by current URI location string
	 * @function
	 * @param {boolean} [force] Even if the same, reload anyway
	 */
	glob.gotoPageByLocation = (force) => {
		glob.gotoPage(glob.getHomePage(), force);
	};

	setTimeout(glob.gotoPageByLocation, 0);

	window.addEventListener('popstate', () => {
		glob.gotoPageByLocation(true);
	});

	/**
	 * Given a page (or the current page) returns its params
	 * @function
	 * @param { GlobPage } [page] Defaults to current page
	 * @return { GlobPageParams }
	 */
	glob.getPageParams = (page = glob.getCurrentPage() ) => {
		return (page?.params || {});
	};

	/**
	 * Given a page (or the current page) returns a named param value
	 * @function
	 * @param { string } pname Parameter name
	 * @param { GlobPage } [page] Defaults to current page
	 * @return { any }
	 */
	glob.getPageParam = (pname, page = glob.getCurrentPage()) => {
		const mp = glob.getPageParams( page );
		return mp[pname];
	};

	/**
	 * @function
	 * @param { GlobPageParams } params
	 * @return { void }
	 * @description Set all the params of the current page
	 */
	glob.setCurrentPageParams = (params= {} ) => {
		const mo = glob.getCurrentPage();
		mo.params = params;
		glob.gotoPage(mo);
	};

	/**
	 * Set the value of a param for current page
	 * @function
	 * @param { string } pname Param name
	 * @param { any } [pvalue] Param value, leave undefined to delete.
	 * @return { void }
	 */
	glob.setCurrentPageParam = (pname, pvalue) => {
		const params = glob.getPageParams();
		if ( typeof pvalue === "undefined" ) {
			delete params[pname];
		} else {
			params[pname] = pvalue;
		}
		glob.setCurrentPageParams(params);
	};

	/**
	 * Remove a param for current page
	 * @function
	 * @param { string } pname Param name
	 * @return { void }
	 */
	glob.rmCurrentPageParam = (pname) => { glob.setCurrentPageParam(pname) };

	/* ------ User prefs -----*/

	/**
	 * Read a preference key
	 * @function
	 * @param {string} key
	 * @return {any}
	 * @see {glob.setPref}
	 */
	glob.getPref = (key) => {
		const p = glob.getStatus('Prefs') || {};
		return p[key];
	};

	/**
	 * Set a Prefs key value, deletes it if value is undefined.
	 * @function
	 * @param {string} key
	 * @param {any} [value]
	 * @return {any}
	 * @see {glob.getPref}
	 */
	glob.setPref = ( key, value ) => {
		const p = glob.getStatus('Prefs') || {};
		if ( typeof value === "undefined" ) {
			delete p[key];
		} else {
			p[key] = value;
		}
		glob.setStatus('Prefs', p, true);
		return value;
	};

	/* ------ Language ------*/

	/**
	 * Get current language
	 * @function
	 * @return {string} 2-characters, lowercase
	 * @see {glob.setLanguage}
	 * @see {glob.getDefaultLanguage}
	 * @see {glob.getLanguageLong}
	 */
	glob.getLanguage = () => (glob.getPref('language') || glob.getDefaultLanguage());

	/**
	 * Get long format of language
	 * @function
	 * @param {string} [lang] Defaults to current language
	 * @return {string} Language in format “xx_XX”
	 * @see {glob.getLanguage}
	 * @see {glob.setLanguage}
	 * @see {glob.getDefaultLanguage}
	 */
	glob.getLanguageLong = ( lang ) => {
		if ( lang && lang.length === 5 ) return lang;
		if ( typeof lang === 'undefined' ) {
			const ll = glob.getPref('languageLong');
			if ( ll ) return ll;
			lang = glob.getLanguage();
		}
		let longlang;
		switch ( lang ) {
			case 'en': longlang = "en-GB"; break;
			default : longlang = lang + '-' + lang.toUpperCase()
		}
		return longlang;
	}

	/**
	 * @function
	 * @return {string} 2-characters, lowercase
	 * @see {glob.getLanguage}
	 * @see {glob.setLanguage}
	 * @see {glob.getLanguageLong}
	 */
	glob.getDefaultLanguage = () => ( glob.prop.labelsDB?.default );

	/**
	 * Set current language, or determines it automatically if parameter undefined
	 * @function
	 * @param {string} [lang] 2-characters, lowercase, undefined to auto-detect
	 * @param {string} [langLong] Language in format “xx_XX”, optional
	 * @return {string} 2-characters, lowercase
	 * @see {glob.getLanguage}
	 * @see {glob.getDefaultLanguage}
	 * @see {glob.getLanguageLong}
	 */
	glob.setLanguage = ( lang, langLong ) => {
		if ( ! glob.prop.labelsDB ) return setTimeout( ()=>{ glob.setLanguage(lang)} );
		const curlanguage = glob.getPref('language');
		if ( typeof lang === "undefined" && curlanguage ) lang = curlanguage;
		if ( typeof lang === "undefined" ) {
			const navl = glob.getStorage("Prefs")?.language;
			if ( navl ) lang = navl;
		}
		if ( typeof lang === "undefined") {
			const ll = window.navigator.language;
			const navl = ll.substring(0,2).toLowerCase();
			if ( glob.prop.labelsDB[navl] ) {
				lang = navl;
				langLong = ll;
			}
		}
		if ( typeof lang === "undefined") {
			(window.navigator.languages||[]).forEach( cl => {
				if ( typeof lang === "string" ) return;
				const navl = cl.substring(0,2).toLowerCase();
				if ( glob.prop.labelsDB[navl] ) {
					lang = navl;
					langLong = cl;
				}
			});
		}
		if ( lang && ! glob.prop.labelsDB[lang] ) lang = undefined;
		if ( typeof lang === "undefined") {
			const ulangs = (window.navigator.languages||[])
				.filter(x => (x && typeof x === "string") )
				.map( x => x.substring(0,2).toLowerCase() );
			const applangs = Object.keys(glob.prop.labelsDB)
				.filter( x => (x.length === 2) );
			lang = ulangs.find( x => ( applangs.includes(x) ) )||glob.prop.labelsDB.default;
		}
		if ( lang !== curlanguage ) {
			if ( ! langLong || langLong.length !== 5 ) langLong = glob.getLanguageLong( lang  );
			window.document.documentElement.setAttribute('lang',langLong);
			const p = glob.getStorage('Prefs') || {};
			p.language = lang;
			p.languageLong = langLong;
			glob.setStatus('Prefs', p, true);
		}
		return lang;
	};

	/**
	 * Converts a key in a localized label
	 * @function
	 * @param {string} key Label key
	 * @param {Object} [values] Optional parameters
	 * @param {string} [lang] Language, defaults to current language
	 * @return {string}
	 */
	glob.label = ( key, values, lang ) => {
		if (typeof key !== "string" ) return "";
		if ( typeof lang === "undefined" ) lang = glob.getLanguage();
		if ( typeof lang === "undefined" ) return "";
		if ( ! (glob.prop.labelsDB && glob.prop.labelsDB[lang]) ) return "";
		let txt = glob.prop.labelsDB[lang][key] ?? glob.prop.labelsDB[glob.prop.labelsDB.default][key];
		if ( typeof txt === "undefined" ) return key;
		if ( values && typeof values === "object" ) {
			Object.entries( values ).forEach( ([k,v])=> {
				const re = new RegExp('\\{\\{'+k+'(\\|[^}]*)?\\}\\}','g');
				txt = txt.replace( re, v );
			});
		}
		txt = txt.replace(/\{\{[^|}]+\|([^}]*)\}\}/g,"$1");
		return txt;
	};

	/**
	 * Labels in “icon” language
	 * @function
	 * @param {string} key Icon key
	 * @return {string}
	 */
	glob.icon = ( key ) => glob.label( key, undefined, 'icon' );

	/**
	 * Copy text to clipboard
	 * @param {string} text The text to be copied
	 * @return {void}
	 */
	glob.copyToClipboard = ( text) => {
		try {
			window.navigator.clipboard.writeText( text ).then( glob.ok );
		} catch(e) {
			console.error(e);
			window.prompt("Cannot copy to clipboard:",text);
		}
	};

	glob.ok = (delay = 500 ) => {
		const myDiv = window.document.createElement('div');
		myDiv.setAttribute('style','position:fixed;top:0;left:0;width:100%;margin-top:20vh;z-index=10000;opacity:.66;text-align:center;');
		myDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" style="width:20vw;fill:#ccc">
			<path d="M10,14v9a1,1,0,0,1-1,1H5V13H9A1,1,0,0,1,10,14Zm15.86182,1.15448-.61036-1.52588A1,1,0,0,0,24.32294,13H18.2215a.5.5,0,0,1-.46814-.6756L18.809,9.50934A2.99909,2.99909,0,0,0,19,8.456V6.5a1.50043,1.50043,0,0,0-1.1123-1.44934.49352.49352,0,0,0-.12751-.01672.51036.51036,0,0,0-.431.23919L15.11255,8.81989q-.11252.18-.24.35L12,13v9.5l.91418.91418A2.00018,2.00018,0,0,0,14.32843,24h7.31751a2,2,0,0,0,1.85693-1.25726l2.37585-6.89093A1.00018,1.00018,0,0,0,25.86182,15.15448Z"/>
		</svg>`;
		window.document.body.appendChild(myDiv);
		setTimeout( ()=>{ myDiv.remove()}, delay);
	}

	// -------- Storage ------

	/**
	 * Returns the content of the storage that the app is currently using
	 * @function
	 * @return {Record<string,any>}
	 */
	glob.getStorageRepo = () => {
		if (glob.prop.applicationStorage) return glob.prop.applicationStorage;
		if (!(window.localStorage || window.sessionStorage)) return glob.prop.applicationStorage = {};
		if (typeof glob.prop.storageCanPersist === 'undefined') {
			const ls = window.localStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined') glob.prop.storageCanPersist = true;
		}
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			const ls = window.localStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined') return glob.prop.applicationStorage = JSON.parse(ls);
			window.localStorage.setItem(glob.prop.keyApplicationName,'{}');
		} else if ( window.sessionStorage ) {
			const ls = window.sessionStorage.getItem(glob.prop.keyApplicationName) ?? undefined;
			if (typeof ls !== 'undefined') return glob.prop.applicationStorage = JSON.parse(ls);
			window.sessionStorage.setItem(glob.prop.keyApplicationName,'{}');
		}
		return glob.prop.applicationStorage = {};
	};

	/**
	 * @function
	 * @param {string} item Key name of the property
	 * @return {any}
	 */
	glob.getStorage = ( item ) => glob.getStorageRepo()[item];

	/**
	 * Set the use of localStorage (persist) or sessionStorage (don't persist).
	 * @desc When changing, if already there and not empty the content is moved from one storage to the other.
	 * @function
	 * @param {boolean} [persist] If true use localStorage, otherwise sessionStorage
	 */
	glob.setStoragePersistence = ( persist = false ) => {
		if ( ! (window.localStorage && window.sessionStorage )) return;
		const cs = JSON.stringify(glob.getStorageRepo());
		if ( persist ) {
			window.localStorage.setItem( glob.prop.keyApplicationName, cs );
			window.sessionStorage.removeItem(glob.prop.keyApplicationName);
		} else {
			window.sessionStorage.setItem( glob.prop.keyApplicationName, cs );
			window.localStorage.removeItem(glob.prop.keyApplicationName);
		}
		glob.prop.storageCanPersist = !! persist;
	};

	/**
	 * Set the value of a storage key, delete it if value is undefined
	 * @function
	 * @param {string} item Key name of the property
	 * @param {any} [value]
	 * @return {void}
	 */
	glob.setStorage = ( item, value ) => {
		if ( ! (window.localStorage || window.sessionStorage )) return;
		const s = glob.getStorageRepo();
		if ( typeof value === 'undefined') {
			delete s[item];
		} else {
			s[item] = value;
		}
		glob.prop.applicationStorage = s;
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			window.localStorage.setItem(glob.prop.keyApplicationName, JSON.stringify(s));
		} else  if ( window.sessionStorage ) {
			window.sessionStorage.setItem(glob.prop.keyApplicationName, JSON.stringify(s));
		}
	}

	/**
	 * Reset and completely remove any track of application storage
	 * @function
	 * @return {void}
	 */
	glob.zapStorage = () => {
		if ( ! (window.localStorage || window.sessionStorage )) return;
		if ( window.localStorage && glob.prop.storageCanPersist ) {
			window.localStorage.removeItem(glob.prop.keyApplicationName);
		} else  if ( window.sessionStorage ) {
			window.sessionStorage.removeItem(glob.prop.keyApplicationName);
		}
	}

	window.glob = glob;

}

/* Author: Marco Balestra <balestra@altersoftware.it> - Y-2022-2025 */
